import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  LinkIcon,
  QrCodeIcon,
  ChatBubbleLeftRightIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

const SETUP_STEPS = [
  {
    title: 'Enable Google review requests',
    body: 'Turn on “Enable Google review requests” below and paste your Google Business Profile review URL (Google Business Profile → Ask for reviews → copy your location link). Save settings.',
  },
  {
    title: 'Share your patient page (optional)',
    body: 'Once saved, copy the patient review page link or print the QR code for reception and treatment rooms. Patients can open it without waiting for an email.',
  },
  {
    title: 'Send after a signed consent',
    body: (
      <>
        On{' '}
        <Link to="/clinic/consents" className="font-medium text-amber-800 underline hover:text-amber-950">
          My Consents
        </Link>{' '}
        (or a consent detail page), use the star “Request Google review” action on a signed consent. The patient’s email is filled in automatically when it is on file.
      </>
    ),
  },
  {
    title: 'Manage private feedback',
    body: 'Patients can leave a public Google review or send private feedback. Private messages appear in the inbox on this page — mark them read, delete when done, or ask happy patients to leave a Google review.',
  },
];

const TIPS = [
  {
    icon: EnvelopeIcon,
    title: 'What patients receive',
    text: 'A short branded email (“How was your visit?”) with a link to your clinic review page — Google review or private feedback.',
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: 'Protect your reputation',
    text: 'Private feedback is never published on Google, but patients must leave an email so you can respond. Name stays optional.',
  },
  {
    icon: ClipboardDocumentListIcon,
    title: 'Track delivery',
    text: 'Consent records show sent → email opened → link opened. On feedback cards you can also see Google clicks and resend follow-ups.',
  },
  {
    icon: QrCodeIcon,
    title: 'Reception tip',
    text: 'Display the QR at checkout so patients can leave a review before they leave — works alongside email requests.',
  },
];

export default function GoogleReviewsClinicGuide({ defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/80 to-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-amber-50/60 transition-colors"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
            <BookOpenIcon className="h-5 w-5 text-amber-700" aria-hidden />
          </span>
          <span>
            <span className="block text-base font-semibold text-gray-900">
              How to use Google Reviews
            </span>
            <span className="block text-sm text-gray-600 mt-0.5">
              Quick setup guide for your practice team
            </span>
          </span>
        </span>
        {open ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-500 shrink-0" aria-hidden />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-500 shrink-0" aria-hidden />
        )}
      </button>

      {open ? (
        <div className="border-t border-amber-100 px-5 pb-6 pt-5 space-y-6">
          <ol className="space-y-4">
            {SETUP_STEPS.map((step, index) => (
              <li key={step.title} className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-600 text-xs font-bold text-white">
                  {index + 1}
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="font-medium text-gray-900">{step.title}</p>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="grid gap-3 sm:grid-cols-2">
            {TIPS.map((tip) => (
              <div
                key={tip.title}
                className="rounded-lg border border-gray-200 bg-white p-4"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <tip.icon className="h-4 w-4 text-amber-600 shrink-0" aria-hidden />
                  <p className="text-sm font-semibold text-gray-900">{tip.title}</p>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 flex gap-3">
            <StarIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <div className="text-sm text-amber-950 leading-relaxed">
              <p className="font-semibold mb-1">Checklist before first send</p>
              <ul className="space-y-1 text-amber-900/90">
                <li className="flex gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" aria-hidden />
                  Feature enabled and Google review URL saved
                </li>
                <li className="flex gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" aria-hidden />
                  Patient has an email on the consent / patient record
                </li>
                <li className="flex gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" aria-hidden />
                  Consent status is signed (request appears on My Consents)
                </li>
              </ul>
              <p className="mt-2 flex items-start gap-1.5 text-amber-900/80">
                <LinkIcon className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                Tip: use a Google link that starts with https and comes from your Business Profile
                (e.g. g.page or maps.google.com).
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
